<template>
  <button class="google-auth-button" data-cy="google-login" @click="handleClick" type="button">
    <img :src="require('@/assets/svg/google-logo.svg')" class="google-icon" alt="Google" />
    <span class="google-button-text">Continue with Google</span>
  </button>
</template>

<script>
import errorHandler from '@/services/errorHandler';
import { toastError } from '@/services/bus';
import { uuid } from '@/utils/uuid';

const GOOGLE_SCOPES = 'email profile openid';
const PENDING_REDIRECT_KEY = 'google-oauth-redirect';
const POPUP_BLOCKED_KEY = 'google-oauth-popup-blocked';
// Long enough that a user with pop-ups switched off never meets the popup again,
// short enough that someone who since allowed them gets the nicer flow back.
const POPUP_BLOCK_MEMORY_MS = 30 * 24 * 60 * 60 * 1000;
// Google always answers a requestCode() with either the callback or the error_callback.
// This only exists so a dropped answer cannot wedge the button shut forever.
const REQUEST_TIMEOUT_MS = 120000;

export default {
  data() {
    return {
      clientId: process.env.GOOGLE_AUTH_CLIENT_ID,
      codeClient: null,
      requestPending: false,
      requestTimeoutId: null
    };
  },
  mounted() {
    this.checkGoogleScript();
    this.handleRedirectResponse();
  },
  beforeDestroy() {
    this.clearPendingRequest();
  },
  methods: {
    checkGoogleScript() {
      const id = 'google-script';

      if (document.getElementById(id)) {
        if (window.google && window.google.accounts) {
          this.setupGoogleAuth();
        } else {
          document.getElementById(id).remove();
          this.addGoogleScript(id);
          this.$trackEvent('google.google-script-reloaded');
        }
      } else {
        this.addGoogleScript(id);
      }
    },
    addGoogleScript(id) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.id = id;

      script.onload = () => {
        this.setupGoogleAuth();
      };

      script.onerror = () => {
        document.getElementById(id).remove();
        errorHandler.captureException('onerror: Could not load the Google script');
        this.$trackEvent('google.google-script-failed');
        toastError('Could not load the Google script, try login with email and password');
      };

      document.head.appendChild(script);
    },
    googleOAuthReady() {
      return !!(window.google && window.google.accounts && window.google.accounts.oauth2);
    },
    setupGoogleAuth() {
      if (!this.googleOAuthReady()) {
        errorHandler.captureException('setupGoogleAuth: Google OAuth is unavailable');
        return;
      }

      // Built here, ahead of any click, so that handleClick has nothing to do
      // but call requestCode() while the user's activation is still live.
      this.codeClient = window.google.accounts.oauth2.initCodeClient({
        client_id: this.clientId,
        scope: GOOGLE_SCOPES,
        ux_mode: 'popup',
        callback: this.handleCodeResponse,
        error_callback: this.handleCodeFlowError
      });
    },
    handleClick() {
      this.$trackEvent('login.google-login-button.click', {
        cta: 'google-button'
      });

      // A popup is already open; a second requestCode() would just orphan the first.
      if (this.requestPending) {
        this.$trackEvent('login.google-login.request-already-pending', { cta: 'google-button' });
        return;
      }

      if (!this.googleOAuthReady()) {
        this.reportFlowUnavailable();
        return;
      }

      // This browser blocked the popup last time. Go straight to the tab redirect
      // rather than spending the click rediscovering it.
      if (this.popupPreviouslyBlocked() && this.redirectAvailable()) {
        this.startRedirectFlow('remembered-block');
        return;
      }

      if (!this.codeClient) {
        this.setupGoogleAuth();
      }

      if (!this.codeClient) {
        this.reportFlowUnavailable();
        return;
      }

      this.startPendingRequest();

      // This call MUST stay in the click handler. Google opens the OAuth popup
      // synchronously from here, and a browser only allows that while the click's
      // transient user activation is still live. Moving it behind any await,
      // timeout or callback is what caused popup_failed_to_open.
      this.codeClient.requestCode();
    },
    reportFlowUnavailable() {
      // Previously this click did nothing at all, silently and unmeasured.
      this.$trackEvent('login.google-login.client-unavailable', { cta: 'google-button' });
      errorHandler.captureException('handleClick: Google code client is unavailable');
      toastError('Could not load the Google script, try login with email and password');
    },
    startPendingRequest() {
      this.requestPending = true;
      this.requestTimeoutId = setTimeout(() => {
        this.requestPending = false;
      }, REQUEST_TIMEOUT_MS);
    },
    clearPendingRequest() {
      this.requestPending = false;
      if (this.requestTimeoutId) {
        clearTimeout(this.requestTimeoutId);
        this.requestTimeoutId = null;
      }
    },
    handleCodeResponse(response) {
      this.clearPendingRequest();

      if (response && response.code) {
        // The popup worked, so stop remembering any earlier block.
        this.forgetPopupBlocked();
        this.$emit('success', { code: response.code });
      } else {
        this.$emit('failure', response || { type: 'missing_code' });
      }
    },
    handleCodeFlowError(error) {
      this.clearPendingRequest();
      const type = error && error.type;

      if (type === 'popup_failed_to_open') {
        this.rememberPopupBlocked();
        this.$trackEvent('login.google-login.popup-blocked', { cta: 'google-button' });

        // Google raises this synchronously from inside requestCode(), while we are
        // still in the click handler, so the user's single click can carry straight
        // on into the tab redirect. They never click twice and never see an error.
        if (this.redirectAvailable()) {
          this.startRedirectFlow('popup-blocked');
          return;
        }

        // Origins that are not registered with Google have nowhere to redirect to;
        // sending them anyway would land the user on a redirect_uri_mismatch page.
        errorHandler.captureException('Google OAuth popup_failed_to_open with no redirect available');
        this.$emit('failure', error);
        return;
      }

      // The user closed the popup. That is a cancellation, not a login failure.
      if (type === 'popup_closed') {
        this.$trackEvent('login.google-login.popup-closed', { cta: 'google-button' });
        return;
      }

      this.$emit('failure', error);
      errorHandler.captureException('Google OAuth code flow error: ' + JSON.stringify(error));
    },
    redirectAvailable() {
      // Set per environment in config/*.env.js. False where this origin cannot be
      // an Authorized redirect URI on the Google client, as on per-branch previews.
      const enabled = process.env.GOOGLE_OAUTH_REDIRECT_ENABLED;
      return enabled !== false && enabled !== 'false';
    },
    redirectPath() {
      const path = (this.$route && this.$route.path) || '/login';
      return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
    },
    startRedirectFlow(reason) {
      if (!this.googleOAuthReady()) {
        this.reportFlowUnavailable();
        return;
      }

      const path = this.redirectPath();
      const state = uuid({ withPrefix: false, length: 36 });

      // Google returns to redirect_uri with only ?code&state, so the route we came
      // from — and its query, which carries platform/UTM/invite context — is parked
      // here and restored on the way back.
      this.writePendingRedirect({
        state,
        path,
        route: {
          name: this.$route.name,
          query: { ...this.$route.query },
          hash: this.$route.hash
        }
      });

      this.$trackEvent('login.google-login.redirect', { cta: 'google-button', reason });

      window.google.accounts.oauth2
        .initCodeClient({
          client_id: this.clientId,
          scope: GOOGLE_SCOPES,
          ux_mode: 'redirect',
          redirect_uri: `${window.location.origin}${path}`,
          state
        })
        // Navigates this tab to Google. Top-level navigation needs no user
        // activation, so this is fine even though the popup was just refused.
        .requestCode();
    },
    handleRedirectResponse() {
      const { code, state, error } = this.$route.query || {};
      if (!code && !error) return;

      const pending = this.readPendingRedirect();
      this.clearPendingRedirect();
      if (!pending) return;

      // Get Google's code and state out of the address bar so a refresh cannot
      // replay them. Deliberately not awaited: the exchange below must not depend
      // on a router navigation completing.
      this.restoreRoute(pending.route);

      if (!state || state !== pending.state) {
        this.$trackEvent('login.google-login.redirect-invalid-state', { cta: 'google-button' });
        errorHandler.captureException('Google OAuth redirect state mismatch');
        return;
      }

      if (error) {
        if (error === 'access_denied') {
          this.$trackEvent('login.google-login.redirect-cancelled', { cta: 'google-button' });
          return;
        }
        this.$trackEvent('login.google-login.redirect-failed', { cta: 'google-button' });
        errorHandler.captureException('Google OAuth redirect error: ' + error);
        return;
      }

      this.$trackEvent('login.google-login.redirect-success', { cta: 'google-button' });
      this.$emit('success', { code, redirectPath: pending.path });
    },
    restoreRoute(route) {
      if (!this.$router || !route) return;
      this.$router.replace(route).catch(() => {});
    },
    writePendingRedirect(pending) {
      try {
        sessionStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify(pending));
      } catch (storageError) {
        errorHandler.captureException('Could not store the Google OAuth redirect state');
      }
    },
    readPendingRedirect() {
      try {
        return JSON.parse(sessionStorage.getItem(PENDING_REDIRECT_KEY));
      } catch (parseError) {
        return null;
      }
    },
    clearPendingRedirect() {
      try {
        sessionStorage.removeItem(PENDING_REDIRECT_KEY);
      } catch (storageError) {
        // Nothing to clean up if storage is unavailable.
      }
    },
    popupPreviouslyBlocked() {
      try {
        const blockedAt = Number(localStorage.getItem(POPUP_BLOCKED_KEY));
        return !!blockedAt && Date.now() - blockedAt < POPUP_BLOCK_MEMORY_MS;
      } catch (storageError) {
        return false;
      }
    },
    rememberPopupBlocked() {
      try {
        localStorage.setItem(POPUP_BLOCKED_KEY, String(Date.now()));
      } catch (storageError) {
        // Without storage the user simply meets the popup again next time.
      }
    },
    forgetPopupBlocked() {
      try {
        localStorage.removeItem(POPUP_BLOCKED_KEY);
      } catch (storageError) {
        // Nothing to clean up if storage is unavailable.
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.google-auth-button {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  background: var(--transparent-white-5);
  border: 1px solid var(--transparent-white-5);
  border-radius: 8px;
  padding: 0 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .google-icon {
    width: 18px;
    height: 18px;
  }

  .google-button-text {
    font-family: 'Mulish', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: var(--transparent-white-60);
  }
}
</style>
